/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../theme';
import { authService } from '../services/authService';
import { itemService } from '../services/itemService';
import { 
  Store, 
  MapPin, 
  Star, 
  MessageSquare, 
  Share2, 
  Filter, 
  Search,
  ChevronRight,
  Image as ImageIcon,
  Clock,
  ShieldCheck,
  ShoppingBag
} from 'lucide-react';
import { ROUTES } from '../routes';

interface Seller {
  id: number;
  firstName: string;
  lastName: string;
  userType: string;
  sellerPlan: string;
  store?: {
    name: string;
    description: string;
    logoUrl: string;
    location: string;
    phone: string;
  };
  createdAt: string;
}

interface Item {
  id: number;
  title: string;
  price: number;
  description: string;
  images?: Image[];
  status: string;
  createdAt: string;
}

interface Image {
  id: number,
  itemId: number
  url: string
}

export default function Storefront() {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const [seller, setSeller] = useState<Seller | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStoreData = async () => {
      try {
        const userResponse = await authService.getUserDetails(id!);
        setSeller(userResponse.data.data);

        // Fetch items by this user
        const itemsResponse = await itemService.listItems({ userId: id });
        setItems(itemsResponse.data.data || []);
      } catch (err: any) {
        console.error('Failed to load storefront.', err);
        if (err.response && err.response.status === 404) {
          setError(`Seller with ID ${id} not found.`);
        } else {
          setError('Failed to load storefront. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchStoreData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.colors.primary }}></div>
      </div>
    );
  }

  if (error || !seller) {
    return (
      <div className="max-w-2xl mx-auto mt-20 p-8 rounded-3xl border text-center space-y-4" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        <Store size={48} className="mx-auto opacity-20" />
        <h2 className="text-2xl font-black" style={{ color: theme.colors.text }}>Seller Not Found</h2>
        <p style={{ color: theme.colors.mutedText }}>{error || "The store you are looking for doesn't exist or has been removed."}</p>
        <Link to={ROUTES.HOME} className="font-bold flex items-center justify-center gap-2 transition-all hover:opacity-70" style={{ color: theme.colors.primary }}>
          Back to Marketplace
        </Link>
      </div>
    );
  }

  const storeDisplayName = seller.store?.name || `${seller.firstName}'s Store`;

  return (
    <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in duration-700 pb-20">
      {/* Enhanced Hero Section */}
      <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl">
        {/* Banner Placeholder */}
        <div className="h-48 md:h-64 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 relative">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>
        </div>
        
        <div className="px-6 md:px-12 pb-10 pt-0 relative" style={{ backgroundColor: theme.colors.surface }}>
          <div className="flex flex-col md:flex-row items-center md:items-end gap-6 -mt-16 md:-mt-20">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-[2rem] border-8 shadow-xl flex-shrink-0 flex items-center justify-center overflow-hidden z-10" 
                 style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.surface }}>
              {seller.store?.logoUrl ? (
                <img src={seller.store.logoUrl} alt={storeDisplayName} className="w-full h-full object-cover" />
              ) : (
                <Store size={64} style={{ color: theme.colors.primary }} opacity={0.2} />
              )}
            </div>
            
            <div className="flex-grow text-center md:text-left mb-2 space-y-2">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                <h1 className="text-3xl md:text-5xl font-black tracking-tighter" style={{ color: theme.colors.text }}>
                  {storeDisplayName}
                </h1>
                <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-lg text-xs font-black">
                  <Star size={14} fill="currentColor" /> 4.8
                </div>
                {seller.sellerPlan === 'Pro' && (
                  <div className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-lg text-xs font-black">
                    <ShieldCheck size={14} /> PRO
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 text-sm font-bold" style={{ color: theme.colors.mutedText }}>
                <span className="flex items-center gap-1.5"><MapPin size={16} className="text-blue-500" /> {seller.store?.location || 'Local Seller'}</span>
                <span className="flex items-center gap-1.5"><Clock size={16} /> Joined {new Date(seller.createdAt).toLocaleDateString()}</span>
                <span className="flex items-center gap-1.5"><ShoppingBag size={16} /> {items.length} Active Listings</span>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-2">
              <button className="p-3 rounded-2xl border hover:bg-gray-50 transition-all" style={{ borderColor: theme.colors.border }}>
                <Share2 size={20} style={{ color: theme.colors.mutedText }} />
              </button>
              <button className="px-8 py-3.5 rounded-2xl font-black text-white transition-all hover:scale-105 shadow-xl shadow-blue-500/20"
                      style={{ backgroundColor: theme.colors.primary }}>
                Follow Store
              </button>
            </div>
          </div>

          <div className="mt-8 max-w-3xl">
            <p className="text-lg leading-relaxed opacity-70" style={{ color: theme.colors.text }}>
              {seller.store?.description || `Welcome to ${storeDisplayName}. Browse our high-quality local listings and message us with any questions!`}
            </p>
          </div>
        </div>
      </div>

      {/* Items Section */}
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-6" style={{ borderColor: theme.colors.border }}>
          <h2 className="text-2xl font-black tracking-tight" style={{ color: theme.colors.text }}>
            Active Listings
          </h2>
          
          <div className="flex items-center gap-3">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-30" size={18} />
              <input 
                type="text" 
                placeholder="Search store..." 
                className="pl-10 pr-4 py-2 rounded-xl border focus:outline-none focus:ring-4 transition-all text-sm w-full md:w-64"
                style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}
              />
            </div>
            <button className="p-2 rounded-xl border hover:bg-gray-50 transition-all" style={{ borderColor: theme.colors.border }}>
              <Filter size={20} style={{ color: theme.colors.mutedText }} />
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="p-20 rounded-[2rem] border border-dashed text-center space-y-4" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <ShoppingBag size={48} className="mx-auto opacity-10" />
            <p className="font-bold text-xl" style={{ color: theme.colors.mutedText }}>This seller has no active listings.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {items.map((item) => (
              <Link 
                key={item.id} 
                to={`${ROUTES.ITEM.replace(':id', item.id.toString())}`} 
                className="group border rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}
              >
                <div className="aspect-[4/5] relative overflow-hidden bg-gray-50">
                  {item.images && item.images[0] ? (
                    <img src={item.images[0].url} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-2 opacity-20">
                      <ImageIcon size={40} />
                      <span className="text-[10px] font-black uppercase tracking-widest">No Image</span>
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-xl font-black text-xs shadow-sm" style={{ color: theme.colors.primary }}>
                      ${Number(item.price).toFixed(0)}
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  <h3 className="font-black text-lg line-clamp-1 group-hover:text-blue-500 transition-colors" style={{ color: theme.colors.text }}>
                    {item.title}
                  </h3>
                  <p className="text-sm opacity-60 line-clamp-2 h-10" style={{ color: theme.colors.text }}>
                    {item.description}
                  </p>
                  <div className="pt-4 flex items-center justify-between border-t" style={{ borderColor: theme.colors.border }}>
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-40">
                      Listed {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                    <ChevronRight size={18} className="opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" style={{ color: theme.colors.primary }} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      
      {/* Contact Banner */}
      <div className="p-10 rounded-[3rem] text-center space-y-6 shadow-xl relative overflow-hidden" 
           style={{ backgroundColor: theme.colors.primary }}>
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <MessageSquare size={300} className="absolute -bottom-20 -right-20 rotate-12 text-white" />
        </div>
        <div className="relative z-10 max-w-xl mx-auto space-y-4">
          <h3 className="text-3xl font-black text-white">Have questions for {seller.firstName}?</h3>
          <p className="text-blue-100 text-lg">Send a direct message to inquire about items, negotiate prices, or arrange a pickup.</p>
          <div className="pt-4">
            <button className="px-12 py-4 rounded-2xl bg-white font-black text-lg transition-all hover:scale-105 shadow-2xl"
                    style={{ color: theme.colors.primary }}>
              Message Seller
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
